import type { Rules } from "odyssey-shared";
import type { InteractTarget } from "../interact-target.ts";
import { HarvestLook, holdsTool } from "./look.ts";
import type { HarvestStore } from "./store.ts";

/**
 * Harvesting as E and the interaction prompt use it (ADR-142 decision 3 and 3a): the prop in reach while a sickle is in
 * hand, and the request for it. The tool is in hand when it is selected on the player's tool belt and the bag holds it too
 * (ADR-142 decision 3b); the action bar holds none. Nothing is offered, and nothing sent, while a hold already runs.
 */

/**
 * What the control reads of the page.
 */
export interface HarvestControlSource {
  readonly rules: Rules;
  readonly selectedTool: () => string | null;
  readonly bag: () => readonly { readonly item: string | null; readonly quantity: number }[];
  readonly chunkSize: () => number;
}

/**
 * The page's harvest control.
 */
export class HarvestControl {
  private readonly look: HarvestLook;

  /**
   * @param store The page's harvest state.
   * @param source What it reads of the page.
   */
  public constructor(
    private readonly store: Pick<HarvestStore, "known" | "isHidden" | "start" | "holding">,
    source: HarvestControlSource,
  ) {
    this.look = new HarvestLook({ marks: () => store.known, hidden: (n) => store.isHidden(n), busy: () => store.holding, holds: (tool) => holdsTool(source.rules, source.selectedTool(), source.bag(), tool), chunkSize: source.chunkSize });
  }

  /**
   * The prop E would harvest for a character standing at a point.
   *
   * @param x The character's feet across, in sixteenths.
   * @param y Its feet down.
   * @returns The target the prompt draws, `found` false when there is none; the same object every call.
   */
  public target(x: number, y: number): InteractTarget {
    return this.look.current(x, y);
  }

  /**
   * Asks the map server to harvest the prop the last `target` named.
   *
   * @returns Whether a request was sent: false with no prop named, while a hold runs, or when the connection would not.
   */
  public start(): boolean {
    return this.look.index >= 0 && this.store.start(this.look.index);
  }
}
